import Phaser from 'phaser';
import { INVENTIONS, type Invention } from '../data/inventions';
import { BIG_THINGS } from '../data/bigThings';
import { unlockedIds } from '../state/milestones';
import { loadScore, elapsedMs, formatElapsed, solvedCount } from '../state/scoreboard';

// Card grid is computed at runtime from canvas width so the scene reflows for
// portrait phones. Desktop = 6 cols × 150-wide cards. Narrow = 3 cols × bigger
// cards. All font sizes are measured in NATIVE canvas units; FIT halves them
// onto a 430px-wide phone, so anything < ~24px native becomes hard to read.
function gridSpec(W: number) {
  const narrow = W < 1100;
  return {
    cardW: narrow ? 250 : 150,
    cardH: narrow ? 290 : 200,
    cols: narrow ? 3 : 6,
    pad: narrow ? 14 : 18,
    narrow,
  };
}

const TYPE_COLOUR: Record<string, number> = {
  health: 0xc94f3e, science: 0x4a8db8, consumer: 0xe6a544,
  tech: 0x6c4ab1, industrial: 0x6e6e6e, agri: 0x6b8c3a,
  defence: 0x4a3e2a, culture: 0xb19b6e,
};

const STATE_TINT: Record<string, number> = {
  WA: 0xa54838, NT: 0x8c3024, QLD: 0x4a7634, SA: 0x986238,
  NSW: 0x3e6638, VIC: 0x3a5a5a, TAS: 0x254842, ACT: 0x6e8a4a,
};

interface CardHandles {
  container: Phaser.GameObjects.Container;
  back: Phaser.GameObjects.Container;
  front: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
}

export class PokedexScene extends Phaser.Scene {
  private revealed = new Set<string>();
  private counter?: Phaser.GameObjects.Text;
  private cards: Map<string, CardHandles> = new Map();
  private detailContainer?: Phaser.GameObjects.Container;
  private scrollContainer?: Phaser.GameObjects.Container;
  private scrollMin = 0;
  private scrollMax = 0;
  private isDragging = false;

  constructor() {
    super({ key: 'Pokedex', active: false });
  }

  create() {
    const W = this.game.config.width as number;
    const H = this.game.config.height as number;
    const g = gridSpec(W);


    // Load any prior session reveals from localStorage.
    try {
      const stored = localStorage.getItem('pa-revealed');
      if (stored) for (const id of JSON.parse(stored)) this.revealed.add(id);
    } catch {}

    this.add.rectangle(W / 2, H / 2, W, H, 0x0d0a08, 0.97).setInteractive();

    // On narrow canvases the cards scroll, so we put a solid header strip on
    // top to hide cards passing behind the title bar. Desktop fits everything,
    // no strip needed. The narrow header carries the run stats + monuments
    // section that lives top-right on desktop, so it's taller than it would
    // be otherwise.
    const headerH = g.narrow ? 410 : 0;
    if (g.narrow) {
      this.add.rectangle(W / 2, headerH / 2, W, headerH, 0x0d0a08, 1).setDepth(8);
      this.add.rectangle(W / 2, headerH, W, 2, 0x4a3e2a, 1).setDepth(8);
    }

    // Title bar.
    // Native sizes are 1.6×–2× what they look like on a phone (FIT halves
    // them at 430px wide), so narrow-mode values are deliberately oversized.
    const titleSize = g.narrow ? '64px' : '46px';
    const sublineSize = g.narrow ? '24px' : '17px';
    const hintSize = g.narrow ? '24px' : '14px';
    const titleY = g.narrow ? 60 : 36;
    const sublineY = g.narrow ? 110 : 76;
    const hintY = g.narrow ? 152 : 110;
    this.add.text(W / 2, titleY, 'Auscyclopedia', {
      fontFamily: 'Fraunces, Georgia, serif', fontSize: titleSize,
      color: '#f2e5c0', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(10);
    this.add.text(W / 2, sublineY, 'things built here', {
      fontFamily: 'JetBrains Mono, monospace', fontSize: sublineSize, color: '#b6a079',
    }).setOrigin(0.5).setDepth(10);
    this.add.text(W / 2, hintY, 'shelf | tap the map to unlock more', {
      fontFamily: 'JetBrains Mono, monospace', fontSize: hintSize, color: '#8a7a5e',
    }).setOrigin(0.5).setDepth(10);

    // Counter — top-right on desktop, but on narrow canvases sits below the
    // hint so it doesn't crowd the title.
    const counterX = g.narrow ? W / 2 : W - 30;
    const counterY = g.narrow ? 200 : 28;
    const counterOriginX = g.narrow ? 0.5 : 1;
    const counterSize = g.narrow ? '28px' : '20px';
    this.counter = this.add.text(counterX, counterY, '', {
      fontFamily: 'JetBrains Mono, monospace', fontSize: counterSize, color: '#ffd966',
    }).setOrigin(counterOriginX, 0.5).setDepth(10);

    // ---- Mobile-only: run stats + monuments row in the header ------------
    // This block lives top-right on the home page on desktop; on mobile we
    // tuck it into The Cabinet so the home page map stays uncluttered.
    if (g.narrow) {
      this.buildNarrowRunPanel(W, 240);
    }

    // Reset button.
    const resetX = g.narrow ? W - 24 : W - 30;
    const resetY = g.narrow ? 60 : 50;
    const resetSize = g.narrow ? '22px' : '14px';
    const resetBtn = this.add.text(resetX, resetY, 'reset', {
      fontFamily: 'JetBrains Mono, monospace', fontSize: resetSize, color: '#8a7a5e',
    }).setOrigin(1, 0.5).setDepth(10).setInteractive({ useHandCursor: true });
    resetBtn.on('pointerover', () => resetBtn.setColor('#b6a079'));
    resetBtn.on('pointerout', () => resetBtn.setColor('#8a7a5e'));
    resetBtn.on('pointerup', () => {
      this.revealed.clear();
      try { localStorage.removeItem('pa-revealed'); } catch {}
      // Re-flip all to back.
      for (const inv of INVENTIONS) this.flipToBack(inv);
      this.updateCounter();
    });

    // Close button.
    const closeSize = g.narrow ? '24px' : '17px';
    const closeBtn = this.add.text(g.narrow ? 24 : 30, 60, '✕ close', {
      fontFamily: 'JetBrains Mono, monospace', fontSize: closeSize, color: '#b6a079',
    }).setOrigin(0, 0.5).setDepth(10).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#f2e5c0'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#b6a079'));
    closeBtn.on('pointerup', () => this.scene.stop());

    // Card grid lives in a scrollable container so portrait phones (where the
    // grid is taller than the canvas) can drag/wheel through it.
    const totalGridW = g.cols * g.cardW + (g.cols - 1) * g.pad;
    const startX = (W - totalGridW) / 2 + g.cardW / 2;
    // First-row CENTER must sit below the header strip + half a card so the
    // top edge isn't clipped by the strip.
    const startY = g.narrow
      ? headerH + g.cardH / 2 + 16
      : 220;
    const rows = Math.ceil(INVENTIONS.length / g.cols);
    const gridH = rows * g.cardH + (rows - 1) * g.pad;
    const visibleH = H - startY - (g.narrow ? 60 : 40);

    const scroll = this.add.container(0, 0).setDepth(5);
    this.scrollContainer = scroll;

    // Mask the scroll area so cards can't draw over the title bar when scrolled.
    const maskTop = g.narrow ? headerH + 2 : startY - g.cardH / 2 - 8;
    const maskShape = this.make.graphics({}, false);
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(0, maskTop, W, H - maskTop - 20);
    scroll.setMask(maskShape.createGeometryMask());

    INVENTIONS.forEach((inv, i) => {
      const col = i % g.cols;
      const row = Math.floor(i / g.cols);
      const x = startX + col * (g.cardW + g.pad);
      const y = startY + row * (g.cardH + g.pad);
      this.makeCard(inv, x, y, scroll);
    });

    // Scroll bounds: container.y can range from minY (showing bottom of grid)
    // to maxY = 0 (showing top). If grid fits the canvas, no scrolling.
    this.scrollMax = 0;
    this.scrollMin = Math.min(0, visibleH - gridH);

    if (gridH > visibleH) {
      this.wireScroll();
      // Subtle scroll-down hint at the bottom edge of the visible area.
      const hint = this.add.text(W / 2, H - 30, '▾  drag to scroll  ▾', {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: g.narrow ? '24px' : '15px',
        color: '#ffd966',
      }).setOrigin(0.5).setDepth(50);
      this.tweens.add({
        targets: hint, alpha: { from: 1, to: 0.4 },
        duration: 1100, yoyo: true, repeat: -1, ease: 'Sine.InOut',
      });
    }

    this.updateCounter();
  }

  /**
   * Mobile-only run panel: time + monuments row, centred under the counter.
   * Wide canvases get this top-right of the home page (in ContinentScene)
   * so we skip it here.
   */
  private buildNarrowRunPanel(W: number, topY: number) {
    const score = loadScore();
    const time = formatElapsed(elapsedMs(score));
    const _solved = solvedCount(score); _solved;
    const unlocked = unlockedIds();

    // Time line — larger on mobile so it's legible at the FIT-scaled
    // canvas size (~0.5× CSS px) without squinting in sunlight.
    this.add.text(W / 2, topY, `time  ${time}`, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '24px', color: '#b6a079',
    }).setOrigin(0.5, 0.5).setDepth(10);

    // Monuments label — sized so it reads at native ~12px CSS on mobile,
    // i.e. just above the platform-recommended minimum.
    const monumentsLabelY = topY + 48;
    this.add.text(W / 2, monumentsLabelY, `M O N U M E N T S    ${unlocked.size} / ${BIG_THINGS.length}`, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '22px', color: '#b19b6e',
    }).setOrigin(0.5, 0.5).setDepth(10);

    // Five icons in a row, centred
    const iconH = 56;
    const iconGap = 14;
    const widths = BIG_THINGS.map((bt) => {
      const key = `bigthing-${bt.id}`;
      const tex = this.textures.exists(key) ? this.textures.get(key) : null;
      const nativeH = tex?.source?.[0]?.height ?? iconH;
      const nativeW = tex?.source?.[0]?.width ?? iconH;
      return (nativeW * iconH) / nativeH;
    });
    const totalW = widths.reduce((a, b) => a + b, 0) + iconGap * (BIG_THINGS.length - 1);
    const rowY = monumentsLabelY + 50;
    let cursorX = W / 2 - totalW / 2;
    BIG_THINGS.forEach((bt, i) => {
      const w = widths[i];
      const cx = cursorX + w / 2;
      const key = `bigthing-${bt.id}`;
      const tex = this.textures.exists(key) ? this.textures.get(key) : null;
      const nativeH = tex?.source?.[0]?.height ?? iconH;
      const scale = nativeH ? iconH / nativeH : 1;
      const icon = this.add.image(cx, rowY, key)
        .setOrigin(0.5)
        .setScale(scale)
        .setDepth(10);

      if (unlocked.has(bt.id)) {
        icon.setTint(0xffffff).setAlpha(1);
        icon.setInteractive({ useHandCursor: true });
        icon.on('pointerover', () => icon.setScale(scale * 1.1));
        icon.on('pointerout', () => icon.setScale(scale));
        icon.on('pointerup', () => {
          this.scene.launch('Milestone', { bigThingId: bt.id, replay: true });
          this.scene.bringToTop('Milestone');
        });
      } else {
        icon.setTint(0x3a2a1a).setAlpha(0.6);
      }

      cursorX += w + iconGap;
    });
  }

  private wireScroll() {
    let dragStartY: number | null = null;
    let dragStartScroll = 0;

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      // Only track grid-area drags (not header taps).
      dragStartY = p.y;
      dragStartScroll = this.scrollContainer?.y ?? 0;
      this.isDragging = false;
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (dragStartY === null || !this.scrollContainer) return;
      const dy = p.y - dragStartY;
      // 10px native = ~5px CSS; enough to distinguish a deliberate drag from
      // finger-jitter on a tap.
      if (Math.abs(dy) > 10) this.isDragging = true;
      if (this.isDragging) {
        this.scrollContainer.y = Phaser.Math.Clamp(
          dragStartScroll + dy, this.scrollMin, this.scrollMax,
        );
      }
    });
    this.input.on('pointerup', () => {
      dragStartY = null;
      // Reset on next tick so card pointerup handlers see the flag.
      this.time.delayedCall(0, () => { this.isDragging = false; });
    });
    this.input.on('wheel', (_p: any, _o: any, _dx: number, dy: number) => {
      if (!this.scrollContainer) return;
      this.scrollContainer.y = Phaser.Math.Clamp(
        this.scrollContainer.y - dy * 0.6, this.scrollMin, this.scrollMax,
      );
    });
  }

  private makeCard(inv: Invention, x: number, y: number, parent?: Phaser.GameObjects.Container) {
    const W = this.game.config.width as number;
    const g = gridSpec(W);
    const cardW = g.cardW;
    const cardH = g.cardH;
    const container = this.add.container(x, y);
    if (parent) parent.add(container);
    const isRevealed = this.revealed.has(inv.id);

    // Clickable bg covers the whole card.
    const bg = this.add.rectangle(0, 0, cardW, cardH, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    container.add(bg);

    // Back face — pub-raffle playing-card style.
    const back = this.add.container(0, 0);
    back.setData('face', 'back');
    const backBg = this.add.rectangle(0, 0, cardW, cardH, 0xa83320)
      .setStrokeStyle(3, 0xffd966);
    back.add(backBg);
    // Inner gold border.
    back.add(this.add.rectangle(0, 0, cardW - 12, cardH - 12, 0xa83320)
      .setStrokeStyle(1, 0xffd966).setFillStyle(0xa83320, 0));
    // Big "?" centered.
    const qSize = g.narrow ? '140px' : '106px';
    back.add(this.add.text(0, -10, '?', {
      fontFamily: 'Fraunces, Georgia, serif', fontSize: qSize,
      color: '#ffd966', fontStyle: 'italic',
    }).setOrigin(0.5));
    // "LOCKED" hint below — stash is read-only, unlocks happen on the map.
    const flipSize = g.narrow ? '24px' : '15px';
    back.add(this.add.text(0, cardH / 2 - (g.narrow ? 32 : 24), 'LOCKED', {
      fontFamily: 'JetBrains Mono, monospace', fontSize: flipSize,
      color: '#ffd966',
    }).setOrigin(0.5));
    container.add(back);

    // Front face — invention details.
    const front = this.add.container(0, 0);
    front.setData('face', 'front');
    const tint = STATE_TINT[inv.origin] ?? 0x4a3e2a;
    const frontBg = this.add.rectangle(0, 0, cardW, cardH, tint).setStrokeStyle(2, 0xb19b6e);
    front.add(frontBg);

    // Card front layout. Card center is y=0; top edge at -cardH/2, bottom at
    // +cardH/2. We carve the card into four stacked regions and anchor each
    // element to the seam between them, so a long name can never crash into
    // the meta line or the type chip.
    //
    //   [ TOP PADDING ]
    //   [   SPRITE    ]   anchored to top, centered horizontally
    //   [    NAME     ]   2-line italic, top-aligned, fills middle
    //   [   META      ]   pinned just above the chip
    //   [   CHIP      ]   pinned to bottom with consistent margin
    //   [ BOT PADDING ]
    const layout = g.narrow
      ? {
          padTop: 12,
          spriteScale: 2.0,
          nameSize: '22px', nameTopFromSprite: 14, nameLineSpacing: 3,
          metaSize: '18px',
          chipW: 120, chipH: 28, chipBottomMargin: 12,
          chipTextSize: '16px',
          metaGapAboveChip: 16,
        }
      : {
          padTop: 8,
          spriteScale: 1.4,
          nameSize: '13px', nameTopFromSprite: 10, nameLineSpacing: 2,
          metaSize: '10px',
          chipW: 70, chipH: 18, chipBottomMargin: 10,
          chipTextSize: '9px',
          metaGapAboveChip: 12,
        };

    const spriteKey = `inv-${inv.id}`;
    const spriteH = 64 * layout.spriteScale;
    const spriteCenterY = -cardH / 2 + layout.padTop + spriteH / 2;

    if (this.textures.exists(spriteKey)) {
      front.add(this.add.image(0, spriteCenterY, spriteKey).setScale(layout.spriteScale));
    } else {
      front.add(this.add.text(0, spriteCenterY, '?', {
        fontFamily: 'Fraunces', fontSize: '46px', color: '#8a7a5e',
      }).setOrigin(0.5));
    }

    const nameTopY = spriteCenterY + spriteH / 2 + layout.nameTopFromSprite;
    front.add(this.add.text(0, nameTopY, inv.name, {
      fontFamily: 'Fraunces, Georgia, serif', fontSize: layout.nameSize,
      color: '#f2e5c0', fontStyle: 'italic',
      wordWrap: { width: cardW - 16 }, align: 'center',
      lineSpacing: layout.nameLineSpacing,
    }).setOrigin(0.5, 0));

    // Chip pinned to bottom; meta sits directly above it.
    const chipCenterY = cardH / 2 - layout.chipBottomMargin - layout.chipH / 2;
    const metaCenterY = chipCenterY - layout.chipH / 2 - layout.metaGapAboveChip
      - parseInt(layout.metaSize, 10) / 2;

    front.add(this.add.text(0, metaCenterY, `${inv.year}  |  ${inv.origin}`, {
      fontFamily: 'JetBrains Mono, monospace', fontSize: layout.metaSize,
      color: '#ead5ac',
    }).setOrigin(0.5));

    const chipColour = TYPE_COLOUR[inv.type] ?? 0x4a3e2a;
    front.add(this.add.rectangle(0, chipCenterY, layout.chipW, layout.chipH, chipColour, 0.95)
      .setStrokeStyle(1, 0x0d0a08, 0.35));
    front.add(this.add.text(0, chipCenterY, inv.type, {
      fontFamily: 'JetBrains Mono, monospace', fontSize: layout.chipTextSize,
      color: '#0d0a08',
    }).setOrigin(0.5));
    container.add(front);

    // Initial visibility.
    back.setVisible(!isRevealed);
    front.setVisible(isRevealed);

    bg.on('pointerover', () => {
      this.tweens.add({ targets: container, scale: 1.05, duration: 120 });
      backBg.setStrokeStyle(3, 0xfff);
      frontBg.setStrokeStyle(2, 0xf2e5c0);
    });
    bg.on('pointerout', () => {
      this.tweens.add({ targets: container, scale: 1.0, duration: 120 });
      backBg.setStrokeStyle(3, 0xffd966);
      frontBg.setStrokeStyle(2, 0xb19b6e);
    });
    bg.on('pointerup', () => {
      // Suppress card clicks if the user was scrolling.
      if (this.isDragging) return;
      // The stash is read-only. Locked cards do nothing on click — the player
      // unlocks new cards by clicking the map (which launches the riddle).
      if (!this.revealed.has(inv.id)) return;
      this.openDetail(inv);
    });

    this.cards.set(inv.id, { container, back, front, bg });
  }

  private flipToBack(inv: Invention) {
    const handles = this.cards.get(inv.id);
    if (!handles) return;
    handles.back.setVisible(true);
    handles.front.setVisible(false);
    handles.container.setScale(1);
  }

  private updateCounter() {
    const n = this.revealed.size;
    const t = INVENTIONS.length;
    const allFound = n === t;
    this.counter?.setText(`${n} / ${t} discovered${allFound ? '  🎉' : ''}`);
    this.counter?.setColor(allFound ? '#88ff66' : '#ffd966');
  }

  private openDetail(inv: Invention) {
    this.detailContainer?.destroy();
    const W = this.game.config.width as number;
    const H = this.game.config.height as number;
    const g = gridSpec(W);

    // ---- Tokens ------------------------------------------------------------
    // Tight, consistent spacing rhythm: padding for outer breathing room,
    // gap-* for vertical relationships between sections.
    const t = g.narrow
      ? {
          padding: 36,
          gapAfterSprite: 28,
          gapTitleToMeta: 14,
          gapMetaToBlurb: 28,
          gapBlurbToDivider: 28,
          gapDividerToDykLabel: 18,
          gapDykLabelToFact: 12,
          gapFactToHint: 22,
          titleSize: '44px',
          metaSize: '22px',
          blurbSize: '26px',
          dykLabelSize: '20px',
          dykSize: '22px',
          hintSize: '16px',
          spriteScale: 3.0,
          metaLineHeight: 28,
          blurbLineSpacing: 8,
          factLineSpacing: 6,
        }
      : {
          padding: 28,
          gapAfterSprite: 0,
          gapTitleToMeta: 10,
          gapMetaToBlurb: 18,
          gapBlurbToDivider: 18,
          gapDividerToDykLabel: 12,
          gapDykLabelToFact: 8,
          gapFactToHint: 14,
          titleSize: '26px',
          metaSize: '12px',
          blurbSize: '15px',
          dykLabelSize: '11px',
          dykSize: '13px',
          hintSize: '10px',
          spriteScale: 2.6,
          metaLineHeight: 16,
          blurbLineSpacing: 6,
          factLineSpacing: 5,
        };

    // ---- Panel sizing ------------------------------------------------------
    // Compute height bottom-up from content so the modal hugs the prose
    // instead of leaving wasted vertical space (the previous layout had a
    // ~150px gap between blurb and "Did you know?").
    const panelW = g.narrow
      ? Math.min(W - 24, 720)
      : Math.min(620, W - 60);

    // Sprite occupies right rail on desktop — text columns must clear it.
    const blurbWrapW = g.narrow
      ? panelW - t.padding * 2
      : panelW - 240;
    const metaWrapW = g.narrow
      ? panelW - t.padding * 2
      : panelW - 240;
    const factWrapW = panelW - t.padding * 2;

    // Pre-measure blurb + fact heights using throwaway hidden Text objects,
    // then dispose. Phaser doesn't expose a measure() so this is the cleanest
    // way to get true wrapped heights.
    const measure = (text: string, fontSize: string, fontFamily: string,
                     wrapW: number, lineSpacing: number, italic = false) => {
      const probe = this.add.text(0, 0, text, {
        fontFamily, fontSize,
        fontStyle: italic ? 'italic' : 'normal',
        wordWrap: { width: wrapW }, lineSpacing,
      }).setVisible(false);
      const h = probe.height;
      probe.destroy();
      return h;
    };

    const titleH = measure(inv.name, t.titleSize, 'Fraunces, Georgia, serif',
      panelW - t.padding * 2, 4, true);
    const blurbH = measure(inv.blurb, t.blurbSize, 'Fraunces, Georgia, serif',
      blurbWrapW, t.blurbLineSpacing, true);
    const factH = measure(inv.fact, t.dykSize, 'JetBrains Mono, monospace',
      factWrapW, t.factLineSpacing);
    const dykLabelH = parseInt(t.dykLabelSize, 10) + 4;
    const hintH = parseInt(t.hintSize, 10) + 4;
    const spritePxH = g.narrow ? 64 * t.spriteScale : 0;

    const contentH = (g.narrow ? spritePxH + t.gapAfterSprite : 0)
      + titleH + t.gapTitleToMeta
      + t.metaLineHeight + t.gapMetaToBlurb
      + (g.narrow ? blurbH : Math.max(blurbH, 64 * t.spriteScale))
      + t.gapBlurbToDivider + 1 /* divider */
      + t.gapDividerToDykLabel + dykLabelH + t.gapDykLabelToFact
      + factH + t.gapFactToHint + hintH;
    const panelH = Math.min(H - 40, contentH + t.padding * 2);

    // ---- Container + dim backdrop -----------------------------------------
    const c = this.add.container(W / 2, H / 2).setDepth(100);
    const dim = this.add.rectangle(0, 0, W, H, 0x000000, 0.65)
      .setInteractive().on('pointerup', () => c.destroy());
    c.add(dim);

    c.add(this.add.rectangle(0, 0, panelW, panelH, 0x1a1713)
      .setStrokeStyle(2, 0xb19b6e));
    // Inner hairline keeps the panel feeling crafted at the seams.
    c.add(this.add.rectangle(0, 0, panelW - 8, panelH - 8, 0x1a1713)
      .setStrokeStyle(1, 0x4a3e2a).setFillStyle(0x1a1713, 0));

    // ---- Sprite (mobile: top centre. desktop: right rail) -----------------
    const spriteKey = `inv-${inv.id}`;
    const hasSprite = this.textures.exists(spriteKey);

    let cursorY = -panelH / 2 + t.padding;

    if (g.narrow) {
      if (hasSprite) {
        const spriteCenterY = cursorY + spritePxH / 2;
        c.add(this.add.image(0, spriteCenterY, spriteKey).setScale(t.spriteScale));
      }
      cursorY += spritePxH + t.gapAfterSprite;
    } else if (hasSprite) {
      // Desktop: sprite sits on the right side; text flows down the left.
      const spriteCenterY = -panelH / 2 + t.padding + (64 * t.spriteScale) / 2;
      c.add(this.add.image(panelW / 2 - 90, spriteCenterY, spriteKey)
        .setScale(t.spriteScale));
    }

    // ---- Title -------------------------------------------------------------
    c.add(this.add.text(-panelW / 2 + t.padding, cursorY, inv.name, {
      fontFamily: 'Fraunces, Georgia, serif', fontSize: t.titleSize,
      color: '#f2e5c0', fontStyle: 'italic',
      wordWrap: { width: panelW - t.padding * 2 }, lineSpacing: 4,
    }).setOrigin(0, 0));
    cursorY += titleH + t.gapTitleToMeta;

    // ---- Meta line ---------------------------------------------------------
    c.add(this.add.text(-panelW / 2 + t.padding, cursorY,
      `${inv.year}  |  ${inv.inventor}  |  ${inv.origin}`, {
      fontFamily: 'JetBrains Mono, monospace', fontSize: t.metaSize,
      color: '#b19b6e',
      wordWrap: { width: metaWrapW },
    }).setOrigin(0, 0));
    cursorY += t.metaLineHeight + t.gapMetaToBlurb;

    // ---- Blurb -------------------------------------------------------------
    c.add(this.add.text(-panelW / 2 + t.padding, cursorY, inv.blurb, {
      fontFamily: 'Fraunces, Georgia, serif', fontSize: t.blurbSize,
      fontStyle: 'italic', color: '#ead5ac',
      wordWrap: { width: blurbWrapW }, lineSpacing: t.blurbLineSpacing,
    }).setOrigin(0, 0));
    cursorY += (g.narrow ? blurbH : Math.max(blurbH, 64 * t.spriteScale))
      + t.gapBlurbToDivider;

    // ---- Divider -----------------------------------------------------------
    c.add(this.add.rectangle(0, cursorY, panelW - t.padding * 2, 1, 0x4a3e2a)
      .setOrigin(0.5, 0));
    cursorY += 1 + t.gapDividerToDykLabel;

    // ---- "Did you know?" ---------------------------------------------------
    c.add(this.add.text(-panelW / 2 + t.padding, cursorY, 'Did you know?', {
      fontFamily: 'JetBrains Mono, monospace', fontSize: t.dykLabelSize,
      color: '#b19b6e',
    }).setOrigin(0, 0));
    cursorY += dykLabelH + t.gapDykLabelToFact;

    c.add(this.add.text(-panelW / 2 + t.padding, cursorY, inv.fact, {
      fontFamily: 'JetBrains Mono, monospace', fontSize: t.dykSize,
      color: '#f2e5c0',
      wordWrap: { width: factWrapW }, lineSpacing: t.factLineSpacing,
    }).setOrigin(0, 0));
    cursorY += factH + t.gapFactToHint;

    // ---- Close hint --------------------------------------------------------
    c.add(this.add.text(0, panelH / 2 - t.padding / 2, 'tap anywhere to close', {
      fontFamily: 'JetBrains Mono, monospace', fontSize: t.hintSize,
      color: '#8a7a5e',
    }).setOrigin(0.5));

    this.detailContainer = c;
  }
}
